import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { usePhoneNumbers } from "@/hooks/use-phone-numbers";
import { useAgent } from "@/services/assistant.query";
import { createCall } from "@/services/outbound-call.service";
import { AgentCombobox } from "@/components/Agents/AgentCombobox";
import { useFieldErrors } from "@/components/Tools/use-field-errors";
import { ApiError } from "@/lib/api-client";
import { Phone, Loader2, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { analyzePrompt } from "@/lib/prompt-template";
import {
  callErrors,
  dialledNumber,
  type PhoneNumberField,
  type Refusal,
  refusalOf,
  refusedFields,
  sentValue,
} from "@/utils/phone-number-rules";

/** The element each field's error sits under, by the field's name. */
const FIELD_ID = { toNumber: "to-number", fromNumber: "from-number", agentId: "agent" } as const;

interface InitiateOutboundCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
}

export default function InitiateOutboundCallDialog({
  open,
  onOpenChange,
  workspaceId,
}: InitiateOutboundCallDialogProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { data: phoneNumbers } = usePhoneNumbers(workspaceId);
  const [toNumber, setToNumber] = useState("");
  // The number itself, as the list returned it: the `call` function knows a
  // number to call from by that, not by its id.
  const [fromNumber, setFromNumber] = useState<string>("");
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  // The picker pages `{ id, name }` rows; the prompt below needs the whole
  // config, so the selected agent is fetched on its own.
  const { data: selectedAgent } = useAgent(selectedAgentId || undefined);
  const [isCalling, setIsCalling] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<Record<string, string>>({});
  const [refusal, setRefusal] = useState<Refusal>();
  const contentRef = useRef<HTMLDivElement>(null);

  // Only active numbers can dial; an inactive one fails at the carrier.
  const outboundPhoneNumbers = useMemo(
    () => phoneNumbers?.filter((phone) => phone.is_active) ?? [],
    [phoneNumbers],
  );

  // A pick that is no longer in the list — the number was deleted while the
  // draft was kept — reads as no pick: the dialog asks for one and sends nothing.
  const listedFromNumber = outboundPhoneNumbers.some((phone) => phone.phone_number === fromNumber)
    ? fromNumber
    : "";

  const form = { toNumber, fromNumber: listedFromNumber, agentId: selectedAgentId };
  const ruleErrors = callErrors(form);
  const errors = { ...ruleErrors, ...refusedFields(refusal, form) };
  const { field, error, check, reset } = useFieldErrors<PhoneNumberField>(errors);
  const agentError = error("agentId", FIELD_ID.agentId);

  // A refusal from the server shows under its field, which may be out of view
  // above a long list of variables: focus goes there once the press is over.
  useEffect(() => {
    if (refusal?.field && !isCalling) {
      document.getElementById(FIELD_ID[refusal.field as keyof typeof FIELD_ID])!.focus();
    }
  }, [refusal, isCalling]);

  // Extract dynamic variables from the selected agent's prompt
  const promptAnalysis = useMemo(
    () => analyzePrompt(selectedAgent?.config?.systemPrompt ?? ""),
    [selectedAgent],
  );
  const promptVariables = promptAnalysis.variables;
  const optionalVariables = useMemo(
    () => new Set(promptAnalysis.optional),
    [promptAnalysis],
  );

  // Reset dynamic variables when agent changes
  useEffect(() => {
    if (selectedAgentId && promptVariables.length > 0) {
      setDynamicVariables((prev) => {
        const initialVars: Record<string, string> = {};
        promptVariables.forEach((varName) => {
          // Preserve existing values if variable still exists in new prompt
          initialVars[varName] = prev[varName] || "";
        });
        return initialVars;
      });
    } else {
      setDynamicVariables({});
    }
  }, [selectedAgentId, promptVariables]);

  // A number usually already knows which agent it dials with, so choosing the
  // number settles the agent too. Only a change of number does this, so an
  // agent picked by hand is never replaced.
  const handlePhoneNumberChange = (value: string) => {
    setFromNumber(value);
    // The column is an FK, so an id on the row is an agent in this workspace;
    // the picker resolves its name without having paged to it.
    const attachedAgentId = outboundPhoneNumbers.find((phone) => phone.phone_number === value)
      ?.outbound_agent_id;
    if (attachedAgentId) setSelectedAgentId(attachedAgentId);
  };

  // With exactly one number to dial from there is nothing to choose, so opening
  // the dialog fills both pickers: that number, and the agent it is set up with.
  useEffect(() => {
    if (!open || outboundPhoneNumbers.length !== 1) return;
    const only = outboundPhoneNumbers[0];
    // Whatever was picked before: with one number left it is the only pick there is.
    setFromNumber(only.phone_number);
    setSelectedAgentId((current) => current || only.outbound_agent_id || "");
  }, [open, outboundPhoneNumbers]);

  // What was typed survives a close, as it always has; the errors the last
  // press put under the fields do not.
  const close = () => {
    setRefusal(undefined);
    reset();
    onOpenChange(false);
  };

  const handleCall = async () => {
    // A refusal from an earlier try no longer applies to what is on screen now.
    setRefusal(undefined);
    // Nothing the server would refuse is sent: the reasons show under their fields.
    if (!check(contentRef.current, ruleErrors)) {
      // `check` focuses the first field marked invalid. The agent picker's
      // trigger takes no such mark, so when it is the first it is focused here.
      if (!ruleErrors.toNumber && !ruleErrors.fromNumber) document.getElementById(FIELD_ID.agentId)!.focus();
      return;
    }

    const cleanedNumber = dialledNumber(toNumber);

    setIsCalling(true);

    try {
      // Prepare dynamic variables object (only include non-empty values)
      const varsToSend: Record<string, string> = {};
      Object.entries(dynamicVariables).forEach(([key, value]) => {
        const sent = sentValue(value);
        if (sent) varsToSend[key] = sent;
      });

      // One request whatever the number's carrier: the `call` function picks
      // the dialer, and whether it waits for an answer.
      await createCall({
        workspace_id: workspaceId,
        to_number: cleanedNumber,
        from_number: listedFromNumber,
        agent_id: selectedAgentId,
        dynamic_variables: Object.keys(varsToSend).length > 0 ? varsToSend : undefined,
      });

      toast({
        title: "Call initiated",
        description: `Calling ${cleanedNumber}...`,
      });

      // Reset form and close dialog
      setToNumber("");
      setFromNumber("");
      setSelectedAgentId("");
      setDynamicVariables({});
      close();
    } catch (failure) {
      const refused = refusalOf(failure, form, "An error occurred while initiating the call.");
      // A value the server refused shows under its field, in the dialog's
      // words: it is never toasted as it came.
      if (refused.field) {
        setRefusal(refused);
        return;
      }

      // No answer at all — offline, say — has no status, and the browser's own
      // words for it ("Failed to fetch") are no use to anyone.
      const status = failure instanceof ApiError ? failure.status : undefined;
      const errorMessage = status
        ? refused.message
        : "Couldn't reach the server. Check your connection and try again.";

      // Check for insufficient balance error
      const isInsufficientBalance =
        status === 402 ||
        errorMessage.toLowerCase().includes('insufficient') ||
        errorMessage.toLowerCase().includes('top up');

      // `call` also answers 403 for a destination the workspace may not dial and
      // for a role that may not place calls: neither is a paused account.
      const isAccountPaused = status === 403 && /paused/i.test(errorMessage);

      toast({
        variant: "destructive",
        title: isInsufficientBalance
          ? "Insufficient Balance"
          : isAccountPaused
            ? "Account Paused"
            : "Failed to initiate call",
        description: (
          <div className="flex flex-col gap-2">
            <span>{errorMessage}</span>
            {isInsufficientBalance && (
              <Button
                variant="outline"
                size="sm"
                className="w-fit mt-1 border-transparent text-foreground shadow-sm"
                onClick={() => {
                  close();
                  navigate(`/${workspaceId}/billing`);
                }}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Go to Billing
              </Button>
            )}
          </div>
        ),
        duration: isInsufficientBalance ? 10000 : 5000,
      });
    } finally {
      setIsCalling(false);
    }
  };

  return (
    // A call in flight finishes in view: a refusal shows under its field, where
    // a closed dialog would hide it.
    <Dialog open={open} onOpenChange={() => !isCalling && close()}>
      <DialogContent
        ref={contentRef}
        className="sm:max-w-[500px] flex flex-col max-h-[90vh] [&_[aria-invalid=true]]:border-destructive"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Initiate Outbound Call
          </DialogTitle>
          <DialogDescription>
            Make an outbound call using your AI agent. Enter the phone number and select an agent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
          <div className="space-y-2">
            <Label htmlFor="to-number">Phone Number to Call</Label>
            <Input
              id="to-number"
              placeholder="+1234567890"
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              disabled={isCalling}
              {...field("toNumber", "to-number")}
            />
            {error("toNumber", "to-number")}
            <p className="text-xs text-muted-foreground">
              Enter phone number in E.164 format (e.g., +1234567890)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="from-number">Call From</Label>
            <Select
              value={listedFromNumber}
              onValueChange={handlePhoneNumberChange}
              disabled={isCalling || outboundPhoneNumbers.length === 0}
            >
              <SelectTrigger id="from-number" {...field("fromNumber", "from-number")}>
                <SelectValue placeholder="Select a phone number" />
              </SelectTrigger>
              <SelectContent>
                {outboundPhoneNumbers.length === 0 ? (
                  <SelectItem value="none" disabled>
                    No active phone numbers available
                  </SelectItem>
                ) : (
                  outboundPhoneNumbers.map((phone) => (
                    <SelectItem key={phone.id} value={phone.phone_number}>
                      {phone.nickname || phone.phone_number}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {error("fromNumber", "from-number")}
            {outboundPhoneNumbers.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Add a phone number in the Phone Numbers page first.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="agent">AI Agent</Label>
            <AgentCombobox
              id={FIELD_ID.agentId}
              workspaceId={workspaceId}
              value={selectedAgentId || null}
              onChange={(id) => setSelectedAgentId(id ?? "")}
              emptyMessage="No agents found. Create one in the Agents page first."
              disabled={isCalling}
              className={agentError ? "border-destructive" : undefined}
            />
            {agentError}
          </div>

          {/* Dynamic Variables Section */}
          {selectedAgentId && promptVariables.length > 0 && (
            <div className="space-y-3 pt-2 border-t">
              <div>
                <Label className="text-sm font-semibold">Dynamic Variables</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Fill in the values for variables used in the agent's prompt (format: {`{{variable_name}}`})
                  <br />
                  <span className="text-xs text-muted-foreground">
                    Note: System variables like {`{{customer_number}}`}, {`{{assistant_number}}`}, and {`{{current_time_[timezone]}}`} are automatically resolved and don't need to be filled.
                  </span>
                  {optionalVariables.size > 0 && (
                    <>
                      <br />
                      <span className="text-xs text-muted-foreground">
                        Variables marked optional are only used inside a {`{% if %}`} condition — leave one blank and the prompt takes its other branch.
                      </span>
                    </>
                  )}
                </p>
              </div>
              {promptVariables.map((varName) => (
                <div key={varName} className="space-y-2">
                  <Label htmlFor={`var-${varName}`} className="text-sm">
                    {varName}
                    {optionalVariables.has(varName) && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">optional</span>
                    )}
                  </Label>
                  <Input
                    id={`var-${varName}`}
                    placeholder={`Enter value for ${varName}`}
                    value={dynamicVariables[varName] || ""}
                    onChange={(e) =>
                      setDynamicVariables((prev) => ({
                        ...prev,
                        [varName]: e.target.value,
                      }))
                    }
                    disabled={isCalling}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button
            variant="outline"
            onClick={close}
            disabled={isCalling}
          >
            Cancel
          </Button>
          <Button onClick={handleCall} disabled={isCalling} aria-busy={isCalling}>
            {isCalling ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Calling...
              </>
            ) : (
              <>
                <Phone className="w-4 h-4 mr-2" />
                Initiate Call
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
